import { reactive, computed, onMounted, onUnmounted } from '@vue/composition-api'
import { debounce } from 'lodash'
import Vue from 'vue'
import useWindow from '@iez/lib/use-window'
import { vuetify } from '@iez/core'

export default ({
  card,
  content,
  onClose
}) => {
  const defaultOffset = 440
  const state = reactive({
    offset: defaultOffset,
    deltaY: 0,
    height: computed(() => state.offset + state.deltaY),
    window: useWindow(),
    fullscreen: false,
    defaultOffset: computed(() => Math.min(defaultOffset, state.contentHeight)),
    contentHeight: 0,
    momentum: 0,
    phase: 0,
    process1: null
  })

  let hammer = null
  onUnmounted(() => {
    if (hammer) {
      hammer.destroy()
      hammer = null
    }
  })

  onMounted(() => {
    const $el = card.value.$el || card.value
    const events = 'panup pandown panend panstart'
    $el.parentNode.style.maxHeight = '100%'
    let scrollTop = 0

    const contentEl = content.value.$el || content.value
    contentEl.addEventListener('scroll', () => {
      scrollTop = contentEl.scrollTop
    })

    hammer = new Hammer($el, {
      inputClass: Hammer.TouchInput
    })
    hammer.get('pan').set({ direction: Hammer.DIRECTION_VERTICAL })

    state.process1 = async () => {
      const lastValue = state.contentHeight
      const compute = () => Math.min(
        Array.from($el.children)
          .reduce((accumulator, value) => accumulator + value.offsetHeight, 0),
        state.window.height
      )
      await Vue.nextTick()
      state.contentHeight = compute()
      if (lastValue === state.contentHeight) {
        await Vue.nextTick()
        state.contentHeight = compute()
      }
      $el.style.transition = 'height .3s'
      if (state.window.height - state.contentHeight < 100) {
        state.offset = state.window.height
        state.fullscreen = true
        state.phase = 1
      } else {
        state.offset = state.contentHeight
        state.fullscreen = false
        if (state.offset < defaultOffset) {
          state.phase = 0
        } else {
          state.phase = 1
        }
      }
    }
    state.process1()

    const handle = debounce(event => {
      const { deltaY, type } = event
      if (state.fullscreen) {
        if (scrollTop || type === 'panup') {
          return
        } else {
          state.fullscreen = false
          state.momentum = deltaY
        }
      }
      $el.style.transition = ''
      state.deltaY = -(deltaY - state.momentum)
      if (type === 'panend') {
        if (state.momentum) {
          state.momentum = 0
        }
        $el.style.transition = 'height .3s'
        if (state.height < state.defaultOffset * 0.6) {
          // to close
          state.offset = 0
          onClose()
        } else if (state.phase === 0 && state.height > state.defaultOffset + state.contentHeight * 0.15) {
          // go to fullscreen
          state.phase = 1
          state.offset = state.window.height
          state.fullscreen = true
        } else if (state.phase === 1 && state.height < state.contentHeight * 0.85) {
          // go to middle step
          state.phase = 0
          state.offset = state.defaultOffset
        }
        state.deltaY = 0
      }
    }, 0)
    hammer.on(events, handle)
  })

  const reset = () => {
    state.process1()
    state.deltaY = 0
    state.fullscreen = false
    state.momentum = 0
    state.phase = 0
    state.offset = state.defaultOffset
  }

  return {
    isClippedTop: computed(() => state.height >= state.window.height),
    height: computed(() => state.height),
    maxContentHeight: computed(() => state.window.height - vuetify.framework.application.top),
    reset
  }
}